interface SupportedCulture {
    display: string;
    englishDisplay: string;
    name: string;
    rcCode: string;
}

export class Culture {
    static defaultCultureCode = 'en-us' as CultureCode;

    static supportedCultures: Record<string, SupportedCulture> = {
        'cs-cz': {
            display: 'Čeština',
            englishDisplay: 'Czech',
            name: 'czech',
            rcCode: 'cs',
        },
        'da-dk': {
            display: 'Dansk',
            englishDisplay: 'Danish',
            name: 'danish',
            rcCode: 'da',
        },
        'de-de': {
            display: 'Deutsch',
            englishDisplay: 'German',
            name: 'german',
            rcCode: 'de',
        },
        'el-gr': {
            display: 'Ελληνικά',
            englishDisplay: 'Greek',
            name: 'greek',
            rcCode: 'el',
        },
        'en-ca': {
            display: 'English',
            englishDisplay: 'English',
            name: 'englishCA',
            rcCode: 'en-US', // treat en-ca as en-US for services and contentful
        },
        'en-us': {
            display: 'English',
            englishDisplay: 'English',
            name: 'englishUS',
            rcCode: 'en-US',
        },
        'es-es': {
            display: 'Español (España)',
            englishDisplay: 'Spanish (Spain)',
            name: 'spanishEuropean',
            rcCode: 'es-ES',
        },
        'es-mx': {
            display: 'Español (Mexico)',
            englishDisplay: 'Spanish (Mexico)',
            name: 'spanishMexico',
            rcCode: 'es-MX',
        },
        'es-us': {
            display: 'Español (US)',
            englishDisplay: 'Spanish (US)',
            name: 'spanishUnitedStates',
            rcCode: 'es-US',
        },
        'fi-fi': {
            display: 'Suomi',
            englishDisplay: 'Finnish',
            name: 'finnish',
            rcCode: 'fi',
        },
        'fr-ca': {
            display: 'Français (Canada)',
            englishDisplay: 'French (Canada)',
            name: 'frenchCanada',
            rcCode: 'fr-CA',
        },
        'fr-fr': {
            display: 'Français (France)',
            englishDisplay: 'French (France)',
            name: 'frenchEuropean',
            rcCode: 'fr-FR',
        },
        'it-it': {
            display: 'Italiano',
            englishDisplay: 'Italian',
            name: 'italian',
            rcCode: 'it',
        },
        'nb-no': {
            display: 'Norsk',
            englishDisplay: 'Norwegian',
            name: 'norwegian',
            rcCode: 'nb',
        },
        'nl-nl': {
            display: 'Nederlands',
            englishDisplay: 'Dutch',
            name: 'dutchTheNetherlands',
            rcCode: 'nl',
        },
        'pl-pl': {
            display: 'Polski',
            englishDisplay: 'Polish',
            name: 'polish',
            rcCode: 'pl',
        },
        'pt-br': {
            display: 'Português do Brasil',
            englishDisplay: 'Portuguese Brazil',
            name: 'portugueseBrazil',
            rcCode: 'pt-BR',
        },
        'pt-pt': {
            display: 'Português',
            englishDisplay: 'Portuguese',
            name: 'portuguese',
            rcCode: 'pt-PT',
        },
        'ru-ru': {
            display: 'Русский',
            englishDisplay: 'Russian',
            name: 'russian',
            rcCode: 'ru',
        },
        'sv-se': {
            display: 'Svenska',
            englishDisplay: 'Swedish',
            name: 'swedish',
            rcCode: 'sv',
        },
    };

    readonly className = 'Culture';
    code: CultureCode;

    private _locale: { name: string; display: string; englishDisplay: string };

    constructor(code = '') {
        this.code = this.normalizeCultureCode(code);
    }

    get shortCode(): string {
        return (
            this.code?.substring(0, 2) ||
            Culture.defaultCultureCode.substring(0, 2)
        );
    }

    get name(): string {
        return this.locale.name;
    }

    get display(): string {
        return this.locale.display;
    }

    get localizedDisplay(): string {
        return this.locale.englishDisplay;
    }

    private get locale(): {
        name: string;
        display: string;
        englishDisplay: string;
    } {
        if (!this._locale) {
            this._locale = Culture.supportedCultures[this.code];
        }

        return this._locale;
    }

    static isCultureCode(cultureCode: string): cultureCode is CultureCode {
        return cultureCode in Culture.supportedCultures;
    }

    static toRcCultureCode(culture: string): string {
        return Culture.supportedCultures[culture?.toLowerCase()]?.rcCode ?? '';
    }

    toString(): string {
        return this.code;
    }

    matchesCultureCode(compareCode: string, code = this.code): boolean {
        return (compareCode || '').toLowerCase() === (code || '').toLowerCase();
    }

    isMetric(): boolean {
        return !this.matchesCultureCode('en-us');
    }

    private normalizeCultureCode(code: string): CultureCode {
        code = code.toLowerCase();
        return Culture.isCultureCode(code) ? code : Culture.defaultCultureCode;
    }
}

export type CultureCode = keyof typeof Culture.supportedCultures;
